import { useCallback, useEffect, useRef, useState } from 'react';
import axiosInstance from '../../services/axiosConfig';
import { API_ENDPOINTS } from '../../config/api';
import { EsimOrderView, EsimPhase, isIssuingOrder, phaseFromOrder, pollDelayMs, shouldPoll } from './esimOrderState';

function readOrder(body: unknown): EsimOrderView | null {
  const row = body as EsimOrderView & { success?: boolean };
  if (!row || typeof row.status !== 'string' || !row.saleId) return null;
  return row;
}

export function useEsimOrder(saleId: string | undefined): EsimPhase & { retry: () => void } {
  const [phase, setPhase] = useState<EsimPhase>({ kind: 'loading' });
  const [tick, setTick] = useState(0);
  const started = useRef<number | null>(null);
  const attempt = useRef(0);

  const load = useCallback(async (id: string, isPoll: boolean) => {
    try {
      const res = await axiosInstance.get(API_ENDPOINTS.ESIM_ORDER(id), {
        validateStatus: (status) => (status >= 200 && status < 300) || status === 304,
      });
      if (res.status === 304) return;
      const order = readOrder(res.data);
      if (!order) {
        setPhase({ kind: 'not-found' });
        return;
      }
      if (isIssuingOrder(order)) {
        if (started.current == null) started.current = Date.now();
      }
      const elapsed = started.current == null ? 0 : Date.now() - started.current;
      setPhase(phaseFromOrder(order, elapsed));
    } catch (error) {
      const status = (error as { response?: { status?: number } })?.response?.status;
      if (status === 404) {
        setPhase({ kind: 'not-found' });
        return;
      }
      if (!isPoll) setPhase({ kind: 'network' });
    }
  }, []);

  useEffect(() => {
    if (!saleId) {
      setPhase({ kind: 'not-found' });
      return;
    }
    started.current = null;
    attempt.current = 0;
    setPhase({ kind: 'loading' });
    void load(saleId, false);
  }, [saleId, tick, load]);

  useEffect(() => {
    if (!saleId || !shouldPoll(phase.kind)) return;
    let timer = 0;
    const kick = () => {
      if (document.hidden) return;
      if (started.current != null && Date.now() - started.current >= 60_000) {
        setPhase((current) => current.kind === 'preparing' ? { kind: 'delayed', order: current.order } : current);
      }
      attempt.current += 1;
      void load(saleId, true);
    };
    timer = window.setTimeout(kick, pollDelayMs(attempt.current));
    const onVisible = () => {
      if (!document.hidden) kick();
    };
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      window.clearTimeout(timer);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [saleId, phase, load]);

  return { ...phase, retry: () => setTick((n) => n + 1) };
}
